import type { ParsedDemo } from '@disa/demo-core';
import { matchScore, roundWinners, SCHEMA_VERSION } from '@disa/demo-core';
import type { BackendKind, StoreBackend } from './backend';
import { openIndexedDbBackend } from './backends/indexeddb';
import { openOpfsBackend } from './backends/opfs';
import {
  CACHE_BYTE_LIMIT,
  CATALOG_NAME,
  type CatalogEntry,
  type CatalogMeta,
  overflowingKeys,
  parseCatalog,
  type SavedDemo,
  savedDemos,
  serialiseCatalog,
  staleKeys,
  withEntry,
  withoutKeys,
  withUse,
} from './catalog';
import { decodeDemo } from './decode';
import { byteLengthOf, encodeDemo } from './encode';
import { canFingerprint, demoKeyFor } from './fingerprint';
import { forgetCoachNotes } from './note-store';

export interface DemoStore {
  readonly kind: BackendKind;
  /** `${fingerprint}:${SCHEMA_VERSION}` — the identity a cached demo is filed under. */
  keyFor(file: File): Promise<string>;
  read(key: string): Promise<ParsedDemo | null>;
  /** The number of bytes the demo occupies in the cache. */
  write(key: string, demo: ParsedDemo, fileName: string): Promise<number>;
  /**
   * What this device holds, most recently used first, from the catalog already in memory — opening
   * the screen that lists them costs no I/O. Another tab's writes are not in it; the catalog is
   * loaded once per store, and a demo saved elsewhere shows up on the next visit.
   */
  list(): readonly SavedDemo[];
  remove(key: string): Promise<void>;
}

/**
 * First round to last, in seconds — the match rather than the recording, which starts in a warmup
 * nobody is reviewing. `undefined` for a demo with no rounds at all, where there is no match to
 * measure.
 */
function durationOf(demo: ParsedDemo): number | undefined {
  const { rounds } = demo.events;
  const first = rounds.at(0);
  const last = rounds.at(-1);

  if (first === undefined || last === undefined) return undefined;

  return (last.endTick - first.startTick) / demo.header.tickRate;
}

function metaFor(demo: ParsedDemo, fileName: string): CatalogMeta {
  const durationSeconds = durationOf(demo);

  return {
    fileName,
    map: demo.header.map,
    roundCount: demo.events.rounds.length,
    score: matchScore(demo),
    storedAt: Date.now(),
    winners: roundWinners(demo),
    ...(durationSeconds === undefined ? {} : { durationSeconds }),
  };
}

function nameFor(key: string): string {
  return `${encodeURIComponent(key)}.demo`;
}

async function loadCatalog(backend: StoreBackend): Promise<CatalogEntry[]> {
  const bytes = await backend.read(CATALOG_NAME);

  return bytes === null ? [] : parseCatalog(bytes);
}

async function saveCatalog(backend: StoreBackend, entries: readonly CatalogEntry[]): Promise<void> {
  await backend.write(CATALOG_NAME, [serialiseCatalog(entries)]);
}

async function discard(
  backend: StoreBackend,
  entries: readonly CatalogEntry[],
  keys: readonly string[],
): Promise<readonly CatalogEntry[]> {
  for (const key of keys) await backend.remove(nameFor(key));
  await forgetCoachNotes(keys);

  return withoutKeys(entries, keys);
}

async function orphanKeys(
  backend: StoreBackend,
  entries: readonly CatalogEntry[],
): Promise<readonly string[]> {
  const known = new Set(entries.map((entry) => nameFor(entry.key)));
  const names = await backend.list();

  return names
    .filter((name) => name !== CATALOG_NAME && !known.has(name))
    .map((name) => decodeURIComponent(name.replace(/\.demo$/, '')));
}

async function prune(backend: StoreBackend): Promise<CatalogEntry[]> {
  const loaded = await loadCatalog(backend);
  const stale = staleKeys(loaded, SCHEMA_VERSION);
  const orphans = await orphanKeys(backend, loaded);
  const dropped = [...stale, ...orphans];

  if (dropped.length === 0) return loaded;

  const kept = await discard(backend, loaded, dropped);
  await saveCatalog(backend, kept);

  return [...kept];
}

function createStore(backend: StoreBackend, catalog: CatalogEntry[], byteLimit: number): DemoStore {
  let entries: readonly CatalogEntry[] = catalog;

  return {
    kind: backend.kind,
    keyFor: demoKeyFor,

    list: () => savedDemos(entries),

    async read(key) {
      const bytes = await backend.read(nameFor(key));

      if (bytes === null) {
        // An entry whose file has gone — cleared by the browser, or a write that was interrupted.
        // Only an entry the catalog still claims is worth a write here: a key nobody has stored is
        // the ordinary miss on every cold open, and repairing that would write the catalog for it.
        if (entries.some((entry) => entry.key === key)) {
          entries = withoutKeys(entries, [key]);
          await saveCatalog(backend, entries);
        }

        return null;
      }

      try {
        const demo = decodeDemo(bytes);
        entries = withUse(entries, {
          key,
          byteLength: bytes.byteLength,
          lastUsedAt: Date.now(),
        });

        return demo;
      } catch {
        // Any failure to read a container back is a miss, whatever raised it: the entry is
        // unusable, and leaving it in place would cost the same read again on the next visit.
        entries = await discard(backend, entries, [key]);
        await saveCatalog(backend, entries);

        return null;
      }
    },

    async write(key, demo, fileName) {
      const chunks = encodeDemo(demo);
      const byteLength = byteLengthOf(chunks);

      await backend.write(nameFor(key), chunks);
      entries = withEntry(entries, {
        key,
        byteLength,
        lastUsedAt: Date.now(),
        meta: metaFor(demo, fileName),
      });

      entries = await discard(backend, entries, overflowingKeys(entries, byteLimit, key));
      await saveCatalog(backend, entries);

      return byteLength;
    },

    async remove(key) {
      entries = await discard(backend, entries, [key]);
      await saveCatalog(backend, entries);
    },
  };
}

export async function createDemoStore(
  backend: StoreBackend,
  byteLimit = CACHE_BYTE_LIMIT,
): Promise<DemoStore> {
  return createStore(backend, await prune(backend), byteLimit);
}

/**
 * `null` when this browser offers no tier to cache into — no OPFS, no IndexedDB, or no Web Crypto
 * to key an entry with. The caller parses every time and says so; it never fails because of it.
 */
export async function openDemoStore(): Promise<DemoStore | null> {
  if (!canFingerprint()) return null;

  const backend = (await openOpfsBackend()) ?? (await openIndexedDbBackend());

  return backend === null ? null : createDemoStore(backend);
}
